import { Menu, MenuItem } from '@app/components/various/Menu';
import { SeparatorItem } from '@app/components/various/SeparatorItem';
import { useDropdownMenuKeyboardHandler } from '@app/utils/hooks/dropdown/useDropdownMenuKeyboardHandler';
import { isDefined } from '@app/utils/is';
import { Dropdown } from '@typings';
import { without } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useDropdownContext } from '../context/DropdownContext';

import { DropdownMenuButtonItem } from './DropdownMenuButtonItem';
import { DropdownMenuLinkItem } from './DropdownMenuLinkItem';
import { DropdownMenuParentItem } from './DropdownMenuParentItem';
import { DropdownMenuSelectableItem } from './DropdownMenuSelectableItem';

type Props = Dropdown.DropdownMenu & {
  id?: string;
  onClose?: () => void;
};

export const DropdownMenu = ({ items, id, selectedKeys, selectionMode, onClose: closeMenu, onSelectionChange }: Props) => {
  const { t } = useTranslation(['common']);
  const focusedItemRef = React.useRef<HTMLButtonElement | HTMLAnchorElement | null>(null);

  const { menuId, closeDropdown } = useDropdownContext();
  const { focusedItemKey, handleKeyDown } = useDropdownMenuKeyboardHandler({ closeMenu, items, selectedKeys, selectionMode });

  React.useEffect(() => {
    focusedItemRef.current?.focus();
  }, [focusedItemKey]);

  const areAllItemsSelected = items.length === selectedKeys?.length;
  const hasSelectedItems = (selectedKeys?.length ?? 0) > 0;

  const handleSelectableItemClick = (key: string) => () => {
    if (selectionMode === 'single') {
      if (!selectedKeys.includes(key)) {
        onSelectionChange([key]);
      }

      closeDropdown();

      return;
    }

    if (selectedKeys?.includes(key)) {
      onSelectionChange(without([key], selectedKeys));

      return;
    }

    onSelectionChange?.([...selectedKeys, key]);
  };

  const handleSelectAllClick = () => {
    const hasUncheckedItems = items.some(item => !selectedKeys?.includes(item.key) && !item.isDisabled);

    if (hasUncheckedItems) {
      onSelectionChange?.(items.filter(item => !item.isDisabled || selectedKeys.includes(item.key)).map(item => item.key));

      return;
    }

    onSelectionChange?.(items.filter(item => item.isDisabled && selectedKeys.includes(item.key)).map(item => item.key));
  };

  return (
    <Menu id={id ?? menuId} role="menu" className="min-w-48" onKeyDown={handleKeyDown}>
      {selectionMode === 'multiple' && (
        <MenuItem
          key="all"
          ref={focusedItemKey === 'all' ? (focusedItemRef as React.RefObject<HTMLButtonElement>) : undefined}
          role="menuitemcheckbox"
          variant="checkbox"
          aria-checked={
            areAllItemsSelected ? 'true'
            : hasSelectedItems ?
              'mixed'
            : 'false'
          }
          isSelected={areAllItemsSelected}
          isIndeterminate={hasSelectedItems && !areAllItemsSelected}
          isFocused={focusedItemKey === 'all'}
          onClick={handleSelectAllClick}
        >
          <SeparatorItem title={t('common:select_all')} />
        </MenuItem>
      )}
      {items.map(item => {
        const { key } = item;
        const isFocused = key === focusedItemKey;

        if (isDefined(selectionMode)) {
          return (
            <DropdownMenuSelectableItem
              key={key}
              ref={isFocused ? (focusedItemRef as React.RefObject<HTMLButtonElement>) : undefined}
              item={item}
              selectionMode={selectionMode}
              isSelected={selectedKeys?.includes(key)}
              isFocused={isFocused}
              onClick={handleSelectableItemClick(key)}
            />
          );
        }

        if ('menu' in item) {
          return (
            <DropdownMenuParentItem
              key={key}
              item={item}
              ref={isFocused ? (focusedItemRef as React.RefObject<HTMLButtonElement>) : undefined}
              isFocused={isFocused}
            />
          );
        }

        if ('to' in item) {
          return (
            <DropdownMenuLinkItem
              key={key}
              item={item}
              ref={isFocused ? (focusedItemRef as React.RefObject<HTMLAnchorElement>) : undefined}
              isFocused={isFocused}
              onClick={closeDropdown}
            />
          );
        }

        return (
          <DropdownMenuButtonItem
            key={key}
            item={item}
            ref={isFocused ? (focusedItemRef as React.RefObject<HTMLButtonElement>) : undefined}
            isFocused={isFocused}
            onClick={closeDropdown}
          />
        );
      })}
    </Menu>
  );
};
